import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  ClickAwayListener,
  FormControl,
  IconButton,
  Paper,
  Popper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EMPInputField from "../components/EMPInputField/EMPInputField";
import EMPButton from "../components/EMPButton/EMPButton";
import EMPSearchFieldRouting from "../components/EMPSearchField/EMPSearchFieldRouting";
import * as AnalyticsService from "../services/analytics/AnalyticsService";
import { UserContext } from "../context/userContext";

const SEARCH_PLACEHOLDER = "Customer name, email, order ID, or VIN";

const GlobalNavSearch = ({ isFordBrand, styles }) => {
  const [anchorElement, setAnchorElement] = useState(null);
  const [error, setError] = useState(false);
  const iconRef = useRef(null);
  const inputRef = useRef(null);
  const { user } = useContext(UserContext) || {};
  const { searchQuery, routeChange, updateSearchQuery } =
    EMPSearchFieldRouting();
  const isOpen = Boolean(anchorElement);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.querySelector("input")?.focus();
    }
  }, [isOpen]);

  const closeSearch = () => {
    setAnchorElement(null);
    setError(false);
  };

  const toggleSearch = (event) => {
    if (isOpen) {
      closeSearch();
    } else {
      // Hang the panel off the bottom-right of the nav bar, not the icon.
      setAnchorElement(
        event.currentTarget.closest("header") || event.currentTarget
      );
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!searchQuery?.trim()) {
      setError(true);
      return;
    }

    routeChange(event);
    AnalyticsService.trackOnClickEvent(
      "",
      "emp:search:content:search by",
      "fv:emp:search:content action",
      user
    );
    updateSearchQuery("");
    closeSearch();
  };

  return (
    <>
      <IconButton
        ref={iconRef}
        aria-label="Search"
        aria-expanded={isOpen}
        onClick={toggleSearch}
        className={`${styles.navIconButton} ${
          isOpen ? styles.navIconButtonOpen : ""
        }`}
        data-testid="global-nav-search-button"
      >
        <SearchIcon fontSize="small" />
      </IconButton>

      <Popper
        open={isOpen}
        anchorEl={anchorElement}
        placement="bottom-end"
        className={styles.searchPopper}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            // The icon toggles the panel itself.
            if (!iconRef.current?.contains(event.target)) {
              closeSearch();
            }
          }}
        >
          <Paper
            elevation={0}
            className={styles.searchPanel}
            data-testid="global-nav-search-panel"
          >
            <FormControl
              component="form"
              onSubmit={handleSubmit}
              onKeyDown={(event) => event.key === "Escape" && closeSearch()}
              error={error}
              className={styles.searchForm}
            >
              <Box className={styles.searchInput}>
                <EMPInputField
                  ref={inputRef}
                  testId="global-nav-search-input"
                  value={searchQuery}
                  type="text"
                  placeholder={SEARCH_PLACEHOLDER}
                  error={error}
                  helperText={error ? "Input cannot be empty." : ""}
                  startAdornment={<SearchIcon fontSize="small" />}
                  onChange={(event) => {
                    updateSearchQuery(event.target.value);
                    setError(false);
                  }}
                />
              </Box>
              <EMPButton
                testId="global-nav-search-submit"
                submitButton
                variant="contained"
                isFord={isFordBrand}
                sx={{ minWidth: "84px" }}
              >
                Search
              </EMPButton>
            </FormControl>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

GlobalNavSearch.propTypes = {
  isFordBrand: PropTypes.bool,
  styles: PropTypes.objectOf(PropTypes.string).isRequired,
};

GlobalNavSearch.defaultProps = {
  isFordBrand: false,
};

export default GlobalNavSearch;
